import { useMemo } from "react";

import {
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TodoConstants } from "constant";

import { createRecurringTypeLabels, createTodoTypeOptions } from "../helpers";

import DateTimeSelect, { ChevronMultiSelect, ChevronSelect } from "./DateTimeSelect";
import PriorityCheckbox from "./PriorityCheckbox";
import { useTodoForm } from "./useTodoForm";
import { useUserOptions } from "./useUserOptions";
import type { UserOption } from "./useUserOptions";

const { RECURRING_TYPES } = TodoConstants;

interface NewTodoModalProps {
  opened: boolean;
  onClose: () => void;
  preselectedClient?: UserOption;
  lockClient?: boolean;
}

const NewTodoModal = ({
  opened,
  onClose,
  preselectedClient,
  lockClient = false,
}: NewTodoModalProps) => {
  const { t } = useTranslation();
  const recurringTypeLabels = useMemo(() => createRecurringTypeLabels(t), [t]);
  const todoTypeOptions = useMemo(() => createTodoTypeOptions(t), [t]);

  // Form state and logic
  const {
    formData,
    dateTime,
    endDateTime,
    errors,
    updateFormData,
    updateDateTime,
    updateEndDateTime,
    clearError,
    resetForm,
    validateIntervalErrors,
    handleSubmit,
    handleClose,
  } = useTodoForm({ onClose, initialClientId: preselectedClient?.id });

  // User options for dropdowns
  const { employeeSelectOptions, clientSelectOptions } = useUserOptions({
    opened,
    onModalClose: resetForm,
    preselectedClient,
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={900}
      centered
      withCloseButton={false}
      padding="xl"
      radius="md"
    >
      <Stack gap="md">
        <Title order={4} fz={18}>
          {t("todoList.new.title")}
        </Title>

        <Group align="stretch" gap="xl">
          {/* Left side - form fields */}
          <Stack gap="md" style={{ flex: 2 }}>
            <TextInput
              placeholder={t("todoList.new.titlePlaceholder")}
              radius="md"
              size="sm"
              value={formData.title}
              onChange={(e) => {
                updateFormData({ title: e.target.value });
                clearError("title");
              }}
              error={errors.title}
            />
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                {t("todoList.new.assignedTo")}
              </span>
              <ChevronMultiSelect
                placeholder={formData.assignees.length === 0 ? t("select.something") : undefined}
                data={employeeSelectOptions}
                value={formData.assignees}
                onChange={(value) => {
                  updateFormData({ assignees: value });
                  clearError("assignees");
                }}
                flex={1}
                radius="md"
                size="sm"
                error={errors.assignees}
              />
            </Group>
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                {t("todoList.new.client")}
              </span>
              <ChevronSelect
                placeholder={t("todoList.new.clientPlaceholder")}
                data={clientSelectOptions}
                value={formData.client}
                onChange={(value) => updateFormData({ client: value })}
                clearable={!lockClient}
                disabled={lockClient}
                flex={1}
                radius="md"
                size="sm"
                error={errors.client}
              />
            </Group>
            {formData.assignees.length > 1 && (
              <Group wrap="nowrap">
                <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                  {t("todoList.new.mustComplete")}
                </span>
                <Radio.Group
                  value={formData.assigneeType}
                  onChange={(value) => updateFormData({ assigneeType: value })}
                >
                  <Group>
                    <Radio
                      value="everyone"
                      label={t("todoList.completionTypes.everyone")}
                      size="xs"
                      styles={{
                        label: { fontSize: 14, cursor: "pointer" },
                        radio: { cursor: "pointer" },
                      }}
                    />
                    <Radio
                      value="anyone"
                      label={t("todoList.completionTypes.anyone")}
                      size="xs"
                      styles={{
                        label: { fontSize: 14, cursor: "pointer" },
                        radio: { cursor: "pointer" },
                      }}
                    />
                  </Group>
                </Radio.Group>
              </Group>
            )}
            <Group wrap="nowrap">
              <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                {t("todoList.new.timeType")}
              </span>
              <Radio.Group
                value={formData.timeType}
                onChange={(value) => updateFormData({ timeType: value })}
              >
                <Group>
                  <Radio
                    value="deadline"
                    label={t("todoList.timeTypes.deadline")}
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                  <Radio
                    value="exact"
                    label={t("todoList.timeTypes.exact")}
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                  <Radio
                    value="interval"
                    label={t("todoList.timeTypes.interval")}
                    size="xs"
                    styles={{
                      label: { fontSize: 14, cursor: "pointer" },
                      radio: { cursor: "pointer" },
                    }}
                  />
                </Group>
              </Radio.Group>
            </Group>

            {(formData.timeType === "deadline" || formData.timeType === "exact") && (
              <Group wrap="nowrap">
                <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                  {t("todoList.new.dateTime")}
                </span>
                <DateTimeSelect
                  day={dateTime.day}
                  month={dateTime.month}
                  year={dateTime.year}
                  time={dateTime.time}
                  onDayChange={(value) => {
                    updateDateTime({ day: value });
                    clearError("day");
                  }}
                  onMonthChange={(value) => {
                    updateDateTime({ month: value });
                    clearError("month");
                  }}
                  onYearChange={(value) => {
                    updateDateTime({ year: value });
                    clearError("year");
                  }}
                  onTimeChange={(value) => {
                    updateDateTime({ time: value });
                    clearError("time");
                  }}
                  dayError={errors.day}
                  monthError={errors.month}
                  yearError={errors.year}
                  timeError={errors.time}
                />
              </Group>
            )}
            {formData.timeType === "interval" && (
              <>
                <Group wrap="nowrap">
                  <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                    {t("todoList.new.start")}
                  </span>
                  <DateTimeSelect
                    day={dateTime.day}
                    month={dateTime.month}
                    year={dateTime.year}
                    time={dateTime.time}
                    onDayChange={(value) => {
                      updateDateTime({ day: value });
                      updateEndDateTime({ day: value }); // Auto-copy to end date
                      clearError("day");
                      clearError("endDay");
                    }}
                    onMonthChange={(value) => {
                      updateDateTime({ month: value });
                      updateEndDateTime({ month: value }); // Auto-copy to end date
                      clearError("month");
                      clearError("endMonth");
                    }}
                    onYearChange={(value) => {
                      updateDateTime({ year: value });
                      updateEndDateTime({ year: value }); // Auto-copy to end date
                      clearError("year");
                      clearError("endYear");
                    }}
                    onTimeChange={(value) => {
                      const newDateTime = { ...dateTime, time: value };
                      updateDateTime({ time: value });
                      clearError("time");
                      validateIntervalErrors(newDateTime, endDateTime);
                    }}
                    dayError={errors.day}
                    monthError={errors.month}
                    yearError={errors.year}
                    timeError={errors.time}
                  />
                </Group>
                <Group wrap="nowrap">
                  <span style={{ width: 100, flexShrink: 0, fontSize: 14 }}>
                    {t("todoList.new.end")}
                  </span>
                  <DateTimeSelect
                    day={endDateTime.day}
                    month={endDateTime.month}
                    year={endDateTime.year}
                    time={endDateTime.time}
                    onDayChange={(value) => {
                      const newEndDateTime = { ...endDateTime, day: value };
                      updateEndDateTime({ day: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onMonthChange={(value) => {
                      const newEndDateTime = { ...endDateTime, month: value };
                      updateEndDateTime({ month: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onYearChange={(value) => {
                      const newEndDateTime = { ...endDateTime, year: value };
                      updateEndDateTime({ year: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    onTimeChange={(value) => {
                      const newEndDateTime = { ...endDateTime, time: value };
                      updateEndDateTime({ time: value });
                      validateIntervalErrors(dateTime, newEndDateTime);
                    }}
                    dayError={errors.endDay}
                    monthError={errors.endMonth}
                    yearError={errors.endYear}
                    timeError={errors.endTime}
                  />
                </Group>
              </>
            )}
            <Checkbox
              label={t("todoList.recurring.enabled")}
              size="xs"
              styles={{ label: { fontSize: 14, cursor: "pointer" }, input: { cursor: "pointer" } }}
              checked={formData.recurring}
              onChange={(e) => {
                updateFormData({
                  recurring: e.target.checked,
                  recurringType: e.target.checked ? "DAILY" : null,
                });
              }}
            />
            {formData.recurring && (
              <Radio.Group
                value={formData.recurringType}
                onChange={(value) => updateFormData({ recurringType: value })}
                ml="md"
              >
                <Group gap="md">
                  {RECURRING_TYPES.map((type) => (
                    <Radio
                      key={type}
                      value={type}
                      label={recurringTypeLabels[type]}
                      size="xs"
                      styles={{
                        label: { fontSize: 14, cursor: "pointer" },
                        radio: { cursor: "pointer" },
                      }}
                    />
                  ))}
                </Group>
              </Radio.Group>
            )}
            <Textarea
              placeholder={t("todoList.new.descriptionPlaceholder")}
              autosize
              minRows={3}
              maxLength={300}
              radius="md"
              size="sm"
              value={formData.description}
              onChange={(e) => updateFormData({ description: e.target.value })}
            />
          </Stack>

          {/* Right side - type, priority and buttons */}
          <Stack gap="md" justify="space-between" style={{ flex: 1 }}>
            <Stack gap="md">
              <ChevronSelect
                placeholder={t("todoList.new.typePlaceholder")}
                data={todoTypeOptions}
                radius="md"
                size="sm"
                value={formData.todoType}
                onChange={(value) => {
                  updateFormData({ todoType: (value as TodoConstants.TodoType | null) ?? null });
                  clearError("todoType");
                }}
                error={errors.todoType}
              />
              <Stack gap="xs" align="flex-end">
                <PriorityCheckbox
                  label={t("todoList.priority.urgent")}
                  checked={formData.urgent}
                  onChange={(value) => updateFormData({ urgent: value })}
                />
                <PriorityCheckbox
                  label={t("todoList.priority.important")}
                  checked={formData.important}
                  onChange={(value) => updateFormData({ important: value })}
                />
              </Stack>
            </Stack>
            <Group justify="flex-end" gap="xl">
              <Button
                variant="outline"
                color="dark"
                radius="md"
                size="sm"
                fz={14}
                onClick={handleClose}
              >
                {t("todoList.new.cancel")}
              </Button>
              <Button color="green" radius="md" size="sm" fz={14} onClick={handleSubmit}>
                {t("todoList.new.save")}
              </Button>
            </Group>
          </Stack>
        </Group>
      </Stack>
    </Modal>
  );
};

export default NewTodoModal;
